import { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { PasswordInput } from '../ui/password-input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Building2, Home, Zap, CreditCard, ArrowRight, ArrowLeft,
  CheckCircle2, Loader2, Minus, Plus, Car, Clock, Shield,
  TrendingDown, Sparkles, Gift, AlertTriangle, Droplets, Flame, DollarSign
} from 'lucide-react';

const PRICE_BUILDING = 40;
const PRICE_UNIT = 40;

const savingsPerUnit = 120; // Průměrná úspora na ruční práci za jednotku/měsíc

export const RegistrationWizard = ({ onRegister, onDemoRequest, loading }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: '', email: '', password: '',
    company: '', phone: '',
    buildings: 1, units: 5, wantsWallbox: false, wallboxes: 0
  });

  const monthlyTotal = form.buildings * PRICE_BUILDING + form.units * PRICE_UNIT;
  const manuallySaved = form.units * savingsPerUnit;
  const roi = manuallySaved > 0 ? Math.round(((manuallySaved - monthlyTotal) / manuallySaved) * 100) : 0;

  const perUnitPerDay = useMemo(() => {
    if (form.units === 0) return 0;
    return ((form.units * PRICE_UNIT) / form.units / 30).toFixed(1);
  }, [form.units]);

  const steps = [
    { title: 'Co získáte', icon: Sparkles },
    { title: 'Váš plán', icon: Building2 },
    { title: 'Účet', icon: CreditCard },
  ];

  const handleSubmit = () => {
    onRegister({
      name: form.name, email: form.email, password: form.password,
      company: form.company, phone: form.phone,
      planned_buildings: form.buildings, planned_units: form.units,
      wants_wallbox: form.wantsWallbox, planned_wallboxes: form.wallboxes
    });
  };

  return (
    <div className="space-y-5">
      {/* Progress */}
      <div className="flex items-center justify-between mb-1">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2 flex-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              i < step ? 'bg-green-500 text-white' : i === step ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
            }`}>
              {i < step ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
            </div>
            <span className={`text-xs hidden sm:inline ${i === step ? 'font-semibold' : 'text-muted-foreground'}`}>{s.title}</span>
            {i < steps.length - 1 && <div className="flex-1 h-px bg-border mx-2" />}
          </div>
        ))}
      </div>

      {/* Step 0: Value proposition */}
      {step === 0 && (
        <div className="space-y-4" data-testid="wizard-step-0">
          <div className="text-center py-2">
            <h2 className="text-xl font-heading font-bold mb-1">Ušetřete hodiny ruční práce</h2>
            <p className="text-sm text-muted-foreground">
              Automatizujte odečty, vyúčtování a párování plateb. Bez závazků.
            </p>
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">Automatické odečty 24/7</p>
                <p className="text-xs text-muted-foreground">Žádné ruční opisování. Data z měřicího systému v reálném čase.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
              <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-4 h-4 text-red-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">Hlídání úniků a poruch 24/7</p>
                <p className="text-xs text-muted-foreground">Únik vody, plynu, elektřiny. Okamžitý alert na email a v aplikaci.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
              <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Zap className="w-4 h-4 text-blue-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">Vyúčtování na 2 kliknutí</p>
                <p className="text-xs text-muted-foreground">PDF s QR kódem. Automatické párování plateb přes Fio banku.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-card border-green-500/30">
              <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-4 h-4 text-green-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">Vydělávejte na Wallboxu</p>
                <p className="text-xs text-muted-foreground">Zveřejněte wallbox přes energomat – zákazníci platí QR kódem, vy inkasujete.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
              <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <TrendingDown className="w-4 h-4 text-green-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">Od {perUnitPerDay} Kč/den za jednotku</p>
                <p className="text-xs text-muted-foreground">Levnější než 10 minut práce účetní. Zaplatí se z první úspory.</p>
              </div>
            </div>
          </div>

          {/* Social proof */}
          <div className="flex items-center gap-2 p-2.5 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200/50 dark:border-amber-800/50">
            <Shield className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              <strong>Bez rizika</strong> – 5 dní zdarma, zrušení kdykoliv, žádné skryté poplatky.
            </p>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1 h-10" onClick={onDemoRequest} data-testid="wizard-try-demo">
              <Gift className="w-4 h-4 mr-2" />
              Demo zdarma
            </Button>
            <Button className="flex-1 h-10" onClick={() => setStep(1)} data-testid="wizard-continue">
              Vybrat plán
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 1: Choose plan */}
      {step === 1 && (
        <div className="space-y-4" data-testid="wizard-step-1">
          <div>
            <h2 className="text-xl font-heading font-bold">Kolik budov a bytů spravujete?</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Můžete kdykoliv přidat nebo ubrat. Platíte jen za to, co používáte.</p>
          </div>

          {/* Buildings counter */}
          <div className="flex items-center justify-between p-3.5 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-primary" />
              <div>
                <p className="font-semibold text-sm">Budovy</p>
                <p className="text-[11px] text-muted-foreground">{PRICE_BUILDING} Kč/měsíc</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setForm(f => ({...f, buildings: Math.max(1, f.buildings - 1)}))} disabled={form.buildings <= 1}>
                <Minus className="w-3.5 h-3.5" />
              </Button>
              <span className="w-9 text-center text-lg font-bold kpi-value">{form.buildings}</span>
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setForm(f => ({...f, buildings: f.buildings + 1}))}>
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Units counter - PO 1 */}
          <div className="flex items-center justify-between p-3.5 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <Home className="w-5 h-5 text-blue-500" />
              <div>
                <p className="font-semibold text-sm">Jednotky (byty)</p>
                <p className="text-[11px] text-muted-foreground">{PRICE_UNIT} Kč/měsíc za jednotku</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setForm(f => ({...f, units: Math.max(1, f.units - 1)}))} disabled={form.units <= 1}>
                <Minus className="w-3.5 h-3.5" />
              </Button>
              <span className="w-9 text-center text-lg font-bold kpi-value">{form.units}</span>
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setForm(f => ({...f, units: f.units + 1}))}>
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Quick add buttons */}
          <div className="flex gap-2">
            {[5, 10, 20, 50].map(n => (
              <Button key={n} variant="outline" size="sm" className="flex-1 h-7 text-xs"
                onClick={() => setForm(f => ({...f, units: n}))}>
                {n} bytů
              </Button>
            ))}
          </div>

          {/* Price summary */}
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Celkem měsíčně</span>
                <div className="text-right">
                  <span className="text-2xl font-bold kpi-value text-primary">{monthlyTotal.toLocaleString('cs-CZ')}</span>
                  <span className="text-sm text-muted-foreground ml-1">CZK</span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5 pt-2 border-t border-primary/10">
                <div className="flex justify-between">
                  <span>{form.buildings} {form.buildings === 1 ? 'budova' : 'budov'} x {PRICE_BUILDING} Kč</span>
                  <span>{(form.buildings * PRICE_BUILDING).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between">
                  <span>{form.units} {form.units === 1 ? 'jednotka' : 'jednotek'} x {PRICE_UNIT} Kč</span>
                  <span>{(form.units * PRICE_UNIT).toLocaleString('cs-CZ')} Kč</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ROI message */}
          {roi > 0 && (
            <div className="flex items-start gap-2 p-2.5 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200/50 dark:border-green-800/50">
              <TrendingDown className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-green-700 dark:text-green-300">
                <strong>Ušetříte ~{manuallySaved.toLocaleString('cs-CZ')} Kč/měsíc</strong> na ruční práci s odečty a vyúčtováním.
                To je <strong>{roi}% návratnost</strong> investice do energomatu.
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" className="h-10" onClick={() => setStep(0)}>
              <ArrowLeft className="w-4 h-4 mr-2" />Zpět
            </Button>
            <Button className="flex-1 h-10" onClick={() => setStep(2)} data-testid="wizard-to-account">
              Pokračovat
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Create account */}
      {step === 2 && (
        <div className="space-y-3.5" data-testid="wizard-step-2">
          <h2 className="text-xl font-heading font-bold">Poslední krok</h2>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Jméno *</Label>
              <Input className="h-9" placeholder="Jan Novák" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required data-testid="wizard-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Firma</Label>
              <Input className="h-9" placeholder="Název s.r.o." value={form.company} onChange={e => setForm(f => ({...f, company: e.target.value}))} data-testid="wizard-company" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Email *</Label>
            <Input className="h-9" type="email" placeholder="vas@email.cz" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} required data-testid="wizard-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Heslo *</Label>
              <PasswordInput className={`h-9 ${form.password.length > 0 && form.password.length < 8 ? 'border-red-400 focus-visible:ring-red-400' : ''}`} placeholder="Min. 8 znaků" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} required data-testid="wizard-password" />
              {form.password.length > 0 && form.password.length < 8 && (
                <p className="text-[11px] text-red-500" data-testid="password-error">Heslo musí mít alespoň 8 znaků ({form.password.length}/8)</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Telefon</Label>
              <Input className="h-9" type="tel" placeholder="+420 xxx xxx xxx" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} data-testid="wizard-phone" />
            </div>
          </div>

          {/* Compact summary */}
          <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex justify-between items-center">
              <span className="text-sm">{form.buildings} {form.buildings === 1 ? 'budova' : 'budov'}, {form.units} {form.units === 1 ? 'jednotka' : 'jednotek'}</span>
              <span className="font-bold text-primary kpi-value">{monthlyTotal} Kč/měsíc</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Po registraci budete přesměrováni na bezpečnou platbu kartou. Prvních 5 dní zdarma.</p>
          </div>

          {/* Trust signals */}
          <div className="flex items-center gap-4 text-[11px] text-muted-foreground justify-center">
            <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Šifrované spojení</span>
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Zrušení kdykoliv</span>
            <span className="flex items-center gap-1"><Gift className="w-3 h-3" /> 5 dní zdarma</span>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" className="h-10" onClick={() => setStep(1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />Zpět
            </Button>
            <Button className="flex-1 h-10" onClick={handleSubmit} disabled={loading || !form.name || !form.email || form.password.length < 8} data-testid="wizard-register">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {loading ? 'Vytvářím účet...' : 'Začít zdarma'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
